/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { CATEGORY_END_POINT } from 'src/api/category';
import courseAPI, { COURSE_END_POINT } from 'src/api/course';
import Heading from 'src/components/molecules/heading';
import CourseFilter from 'src/components/organisms/courseFilter';
import CourseResult from 'src/components/organisms/courseResult';
import { useQuery, useFilterItems, useWindowSize } from 'src/helpers/use';
import { convertObjectToQueryString } from 'src/helpers/utils';
import { CourseFiltersKey, IBreadcrumb, ICategory } from 'src/interfaces';
import { CourseAPIQueryProps, ISection } from 'src/interfaces/course';
import courseActions from 'src/redux/course/actions';
import { RootState } from 'src/redux/reducer';
import theme from 'src/theme';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';

import { Container } from './style';

import { useInsider } from '@hooks/dataTracking/useInsider';

const Sections = dynamic(import('src/components/organisms/sections'));

const { add_course_api_query, clear_course_api_query } = courseActions;

const CategoryCourses = ({ isSubCategory = false }) => {
  const { width = 0 } = useWindowSize();
  const { t } = useTranslation('common');
  const router = useRouter();
  const dispatch = useDispatch();
  const { queryFilters }: { queryFilters: CourseAPIQueryProps } = useSelector((state: RootState) => ({
    queryFilters: state.Course.get('courseApiQueries'),
  }));
  const queryString = useQuery();
  const { data: category } = isSubCategory ? useSWR<ICategory>('SUBCATEGORY') : useSWR<ICategory>('CATEGORY');
  const parent = isSubCategory && useSWR<ICategory>('CATEGORY');

  const { data: sections } = useSWR<ISection[]>(CATEGORY_END_POINT.SECTIONS(category!.id));
  const { filters } = useFilterItems(category?.children, true);
  const initCourseCount = 9; // 12 is original number of first course items to be displayed from jira card
  const loadMoreCount = 3; // 6 is original number of load more count items from jira card

  const { name, description, image } = category || {};

  const headerTitle = t('category.header.title', { categoryName: name });

  const { insider_dispatch, sendListingPageView } = useInsider();
  const insiderRef = useRef<boolean>(false);

  const breadcrumbList = () => {
    const breadcrumbs: IBreadcrumb[] = [{ description: t('main_page'), url: '/' }];
    if (parent && parent.data) {
      breadcrumbs.push({
        description: t('category.breadcrumbs.title', { categoryName: parent.data.name }),
        url: `/categories/${parent.data.slug}`,
      });
    }
    if (!isSubCategory || width >= theme.breakpoints.values.md) {
      breadcrumbs.push({
        description: t('category.breadcrumbs.title', { categoryName: category?.name }),
      });
    }
    return breadcrumbs;
  };

  const headerDesc = description;

  const courseApiResponse = useSWRInfinite((pageIndex, previousPageData) => {
    if (!category || (previousPageData && !previousPageData.data.items.length)) return null;
    const limit = pageIndex === 0 ? initCourseCount : loadMoreCount;
    const offset = (() => {
      if (pageIndex !== 0) return pageIndex === 1 ? initCourseCount : limit * (pageIndex - 1) + initCourseCount;
      return 0;
    })();
    let url = `${COURSE_END_POINT.COURSES}?limit=${limit}&offset=${offset}&${CourseFiltersKey.CATEGORY}=${category.id}`;
    const apiQueryString = convertObjectToQueryString(queryFilters);
    if (apiQueryString) {
      url += `&${apiQueryString}`;
    }
    return url;
  }, courseAPI.fetchCourses);

  useEffect(() => {
    if (queryString && Object.keys(queryString).length) {
      const convertQuery: Record<string, string[]> = Object.keys(queryString).reduce(
        (acc: Record<string, string[]>, key: string) => {
          acc[key] = (queryString[key] as string)?.split(',');
          return acc;
        },
        {},
      );
      dispatch(add_course_api_query(convertQuery));
    } else if (queryString) {
      dispatch(clear_course_api_query());
    }
  }, [JSON.stringify(queryString)]);

  useEffect(() => {
    if (queryString && Object.keys(queryString).length === 0 && queryString.constructor === Object) {
      dispatch(clear_course_api_query());
    }
    return () => {
      dispatch(clear_course_api_query());
    };
  }, [router.query.slug]);

  useEffect(() => {
    if (!category || insiderRef.current) {
      return;
    }

    insiderRef.current = true;

    insider_dispatch([
      () =>
        sendListingPageView({
          taxonomy: [category.name],
        }),
    ]);

    setTimeout(() => {
      insiderRef.current = false;
    }, 1000);
  }, [category]);

  return useMemo(
    () => (
      <>
        <Heading
          contentHeight="200px"
          breadcrumbList={breadcrumbList()}
          headerTitle={headerTitle}
          headerDesc={headerDesc}
          backgroundImage={!isSubCategory && image}
        />
        <Container sx={{ mt: '1rem', mb: '2rem' }}>
          <Sections sections={sections!} />
          <Grid container spacing={2}>
            <Grid item lg={12}>
              <Typography variant="h2" sx={{ mx: 2, mt: 3 }}>
                {t('category.filter_header.title', { categoryName: name })}
              </Typography>
            </Grid>
            <Grid item lg={3} sx={{ display: { xs: 'none', lg: 'block' } }} className="side-bar">
              <CourseFilter filters={filters} />
            </Grid>
            <Grid item xs={12} lg={9} className="main-content">
              <CourseResult courseApiResponse={courseApiResponse} loadMoreCount={loadMoreCount} isShowSuggestion />
            </Grid>
          </Grid>
        </Container>
        <Divider />
      </>
    ),
    [breadcrumbList, courseApiResponse, filters, headerDesc, headerTitle],
  );
};

export default CategoryCourses;
